<script setup lang="ts">
import { NumberFieldDecrement, NumberFieldIncrement, NumberFieldInput, NumberFieldRoot } from 'reka-ui'
import { computed, useId, useTemplateRef } from 'vue'
import PhMinus from '~icons/ph/minus'
import PhPlus from '~icons/ph/plus'
import { size as sizeConst } from '@/services/application/constantes'

const props = withDefaults(defineProps<{
  label?: string
  size?: keyof typeof sizeConst
  min?: number
  max?: number
}>(), {
  size: 'small',
})

const id = useId()
const fieldRef = useTemplateRef<HTMLElement>('fieldRef')

const numberValue = defineModel<number>()
const emit = defineEmits<{
  blur: [event: FocusEvent]
  focus: [event: FocusEvent]
}>()

function onFieldFocusIn(event: FocusEvent) {
  const current = fieldRef.value
  const previous = event.relatedTarget as Node | null
  if (current && previous && current.contains(previous)) return
  emit('focus', event)
}

function onFieldFocusOut(event: FocusEvent) {
  const current = fieldRef.value
  const next = event.relatedTarget as Node | null
  if (current && next && current.contains(next)) return
  emit('blur', event)
}

const sizeClasses = computed(() => {
  const map: Record<keyof typeof sizeConst, string> = {
    dense: 'h-6 text-xs',
    small: 'h-8 text-sm',
    medium: 'h-10 text-base',
    large: 'h-12 text-lg',
  }
  return map[props.size]
})
</script>

<template>
  <NumberFieldRoot :id="id" v-model="numberValue" :min="props.min" :max="props.max">
    <label
      v-if="label"
      :for="id"
      class="subtitle text-[0.75rem] font-medium leading-6 mb-0.5"
    >
      {{ props.label }}
    </label>
    <div
      ref="fieldRef"
      class="flex items-center border border-base bg-base hover:bg-fade rounded-lg shadow-xs focus-within:shadow-[0_0_0_2px] focus-within:shadow-stone-800"
      :class="sizeClasses"
      @focusin="onFieldFocusIn"
      @focusout="onFieldFocusOut"
    >
      <NumberFieldDecrement class="p-2 disabled:opacity-20 shrink-0">
        <PhMinus />
      </NumberFieldDecrement>
      <NumberFieldInput class="bg-transparent w-full tabular-nums text-center focus:outline-0 p-1" />
      <NumberFieldIncrement class="p-2 disabled:opacity-20 shrink-0">
        <PhPlus />
      </NumberFieldIncrement>
    </div>
  </NumberFieldRoot>
</template>
